import './leaderboard-item.scss';

import { type CSSProperties, memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { LeaderboardEntry, LeaderboardSortBy } from '@/entities/leaderboard';
import CupIcon from '@/shared/assets/icons/cup.svg?react';
import FireIcon from '@/shared/assets/icons/fire.svg?react';
import TonIcon from '@/shared/assets/icons/ton.svg?react';
import { PlayerAvatar } from '@/shared/ui/PlayerAvatar';

const formatMetric = (sortBy: LeaderboardSortBy, entry: LeaderboardEntry): string => {
  if (sortBy === 'games') return String(entry.totalGames);
  const value = sortBy === 'profit' ? entry.maxWin : entry.totalWagered;
  if (Math.abs(value) >= 100) return value.toFixed(1);
  return value.toFixed(2);
};

const getItemMod = (rank: number) => {
  if (rank <= 3) return `top${rank}`;
  if (rank <= 10) return 'rank4';
  if (rank <= 50) return 'rank11';
  return '';
};

function RankBadge({ rank }: { rank: number }) {
  if (rank <= 3) {
    const mod = rank === 1 ? 'gold' : rank === 2 ? 'silver' : 'bronze';
    return (
      <div className={`leaderboard-item__rank-badge leaderboard-item__rank-badge--${mod}`}>
        <CupIcon className="leaderboard-item__rank-trophy" />
      </div>
    );
  }
  return <span className="leaderboard-item__rank-num">#{rank}</span>;
}

type LeaderboardItemProps = {
  entry: LeaderboardEntry;
  sortBy: LeaderboardSortBy;
  index: number;
};

function LeaderboardItemComponent({ entry, sortBy, index }: LeaderboardItemProps) {
  const { t } = useTranslation();
  const mod = getItemMod(entry.rank);

  return (
    <div
      className={`leaderboard-item${mod ? ` leaderboard-item--${mod}` : ''}`}
      style={{ '--item-in-delay': `${index * 0.015}s` } as CSSProperties}>
      <div className="leaderboard-item__rank">
        <RankBadge rank={entry.rank} />
      </div>
      <div className={`leaderboard-item__avatar leaderboard-item__avatar--${mod || 'default'}`}>
        <PlayerAvatar id={entry.userId} avatar={entry.avatar} size={34} level={0} />
      </div>
      <div className="leaderboard-item__info">
        <span className="leaderboard-item__username">
          {entry.username ?? t('leaderboard.unknownPlayer')}
        </span>
        <span className="leaderboard-item__meta">
          {entry.totalGames} {t('leaderboard.games')}
          {entry.winsCount > 0 && ` · ${entry.winsCount} ${t('leaderboard.wins')}`}
          {entry.bestStreak > 1 && (
            <span className="leaderboard-item__streak">
              <FireIcon className="leaderboard-item__streak-icon" />
              {entry.bestStreak}
            </span>
          )}
        </span>
      </div>
      <span className={`leaderboard-item__metric leaderboard-item__metric--${sortBy}`}>
        {sortBy !== 'games' && <TonIcon className="leaderboard-item__metric-icon" />}
        {formatMetric(sortBy, entry)}
      </span>
    </div>
  );
}

export const LeaderboardItem = memo(LeaderboardItemComponent);
